import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const getNetworkErrorMessage = (error) => {
  if (!error) return 'An unknown error occurred.';
  if (
    error instanceof TypeError ||
    error?.name === 'AbortError' || error?.message?.toLowerCase()?.includes('fetch') ||
    error?.message?.toLowerCase()?.includes('network') ||
    error?.message?.toLowerCase()?.includes('timed out')
  ) {
    return 'Unable to connect to the server. Your Supabase project may be paused — visit supabase.com/dashboard to resume it.';
  }
  return error?.message || 'An unexpected error occurred. Please try again.';
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    supabase?.auth?.getSession()?.then(({ data: { session }, error }) => {
      if (!mounted) return;
      if (error) {
        console.warn('[Auth] getSession error:', error?.message);
      }
      setUser(session?.user ?? null);
      setLoading(false);
    })?.catch((err) => {
      if (!mounted) return;
      console.warn('[Auth] getSession failed:', err?.message);
      setLoading(false);
    });

    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((_event, session) => {
      if (!mounted) return;
      setUser(session?.user ?? null);
    });

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  const signUp = async (email, password, metadata = {}) => {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: { data: metadata }
      });
      if (error) {
        return { data: null, error: { message: error?.message } };
      }
      return { data, error: null };
    } catch (err) {
      return { data: null, error: { message: getNetworkErrorMessage(err) } };
    }
  };

  const signIn = async (email, password) => {
    try {
      let result;
      try {
        result = await supabase?.auth?.signInWithPassword({ email, password });
      } catch (fetchErr) {
        return { data: null, error: { message: getNetworkErrorMessage(fetchErr) } };
      }
      const { data, error } = result || {};
      if (error) {
        return { data: null, error: { message: error?.message } };
      }
      return { data, error: null };
    } catch (err) {
      return { data: null, error: { message: getNetworkErrorMessage(err) } };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut();
      if (error) {
        return { error: { message: error?.message } };
      }
      return { error: null };
    } catch (err) {
      return { error: { message: getNetworkErrorMessage(err) } };
    }
  };

  const resetPassword = async (email) => {
    try {
      const { data, error } = await supabase?.auth?.resetPasswordForEmail(email);
      if (error) {
        return { data: null, error: { message: error?.message } };
      }
      return { data, error: null };
    } catch (err) {
      return { data: null, error: { message: getNetworkErrorMessage(err) } };
    }
  };

  const updateUserMetadata = async (metadata) => {
    try {
      const { data, error } = await supabase?.auth?.updateUser({ data: metadata });
      if (error) {
        return { data: null, error: { message: error?.message } };
      }
      return { data, error: null };
    } catch (err) {
      return { data: null, error: { message: getNetworkErrorMessage(err) } };
    }
  };

  const value = {
    user,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updateUserMetadata,
    isEmailVerified: user?.email_confirmed_at != null
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
